/**
 * commerce/order/confirm/v1 的轮询封装：首次 confirm 非 PAID 时继续按固定次数 / 间隔轮询。
 * 被 PayPal checkout-url 整页跳转回跳后的 confirm 流程复用。
 *
 * 容错策略：
 * - confirm 返回 status === 'PAID'：立刻返回；
 * - confirm 返回非 PAID（任何状态）：继续轮询；
 * - confirm 返回 code === INTERNAL_SERVER_ERROR (900001)：视为致命失败，立刻终止轮询并向上抛；
 * - 通用 fetch 在 HTTP 200 + 业务 code !== 0 时会 reject（throw AxiosResponse），
 *   此处统一从 reject 体中解析 { code, message, data } 再走 assertConfirmOrderResponse；
 * - 其它抛错（网络抖动、暂不可用等）：继续轮询，全部失败后再抛最后一次错误。
 */

import { EApiBaseCode } from '@/constants';

import type { IConfirmOrderData } from '@/apis/commerce';

/** 首笔 confirm 非 PAID 时，额外轮询次数 */
const COMMERCE_CONFIRM_EXTRA_POLLS = 5;
/** 轮询间隔（暂固定 2s） */
const COMMERCE_CONFIRM_POLL_INTERVAL_MS = 2000;

function sleepMs(ms: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}

/** confirm 接口返回 900001 等不可重试错误时使用 */
export class ConfirmOrderFatalError extends Error {
    readonly code: number;

    constructor(message: string, code: number) {
        super(message);
        this.name = 'ConfirmOrderFatalError';
        this.code = code;
    }
}

export function isConfirmOrderFatalError(err: unknown): err is ConfirmOrderFatalError {
    return err instanceof ConfirmOrderFatalError;
}

interface IConfirmOrderApiResponse {
    code: number;
    message?: string;
    data?: IConfirmOrderData | null;
}

/** 校验 confirm 响应：SUCCESS 则返回 data；900001 抛 ConfirmOrderFatalError；其它非 SUCCESS 抛普通 Error */
export function assertConfirmOrderResponse(res: IConfirmOrderApiResponse): IConfirmOrderData {
    if (res.code === EApiBaseCode.SUCCESS && res.data) {
        return res.data;
    }
    if (res.code === EApiBaseCode.INTERNAL_SERVER_ERROR) {
        throw new ConfirmOrderFatalError(res.message || 'internal_server_error', res.code);
    }
    throw new Error(res.message || 'confirm failed');
}

export interface IConfirmCommerceOrderArgs {
    commerceOrderId: string;
    paypalOrderId: string;
}

function isBusinessResponseBody(value: unknown): value is IConfirmOrderApiResponse {
    if (value === null || typeof value !== 'object') {
        return false;
    }
    return typeof (value as IConfirmOrderApiResponse).code === 'number';
}

/**
 * 从通用 fetch reject 中解析业务响应体。
 * - HTTP 200 + 业务 code !== 0：fetch throw AxiosResponse，业务体在 err.data
 * - HTTP 非 2xx 等 AxiosError：业务体在 err.response.data
 */
function extractFetchBusinessResponse(err: unknown): IConfirmOrderApiResponse | null {
    if (err === null || typeof err !== 'object') {
        return null;
    }

    const directData = (err as { data?: unknown }).data;
    if (isBusinessResponseBody(directData)) {
        return directData;
    }

    const responseData = (err as { response?: { data?: unknown } }).response?.data;
    if (isBusinessResponseBody(responseData)) {
        return responseData;
    }

    return null;
}

interface IConfirmAttemptState {
    lastResult: IConfirmOrderData | null;
    lastError: unknown;
}

/**
 * 统一处理 confirm 单次尝试的 reject：
 * - 已是 ConfirmOrderFatalError → 立刻向上抛；
 * - fetch reject 且能解析出业务体 → 走 assertConfirmOrderResponse（900001 会转为 fatal）；
 * - 其它 → 标记为可重试。
 * 返回 true 表示已 PAID，可结束轮询。
 */
function applyConfirmAttemptError(err: unknown, attemptState: IConfirmAttemptState): boolean {
    if (isConfirmOrderFatalError(err)) {
        throw err;
    }

    const apiRes = extractFetchBusinessResponse(err);
    if (apiRes) {
        try {
            attemptState.lastResult = assertConfirmOrderResponse(apiRes);
            attemptState.lastError = null;
            return attemptState.lastResult.status === 'PAID';
        } catch (assertErr) {
            if (isConfirmOrderFatalError(assertErr)) {
                throw assertErr;
            }
            attemptState.lastError = assertErr;
            return false;
        }
    }

    attemptState.lastError = err;
    return false;
}

export async function confirmCommerceOrderWithPolling(
    confirm: (args: IConfirmCommerceOrderArgs) => Promise<IConfirmOrderData>,
    args: IConfirmCommerceOrderArgs
): Promise<IConfirmOrderData> {
    const attemptState: IConfirmAttemptState = { lastResult: null, lastError: null };

    // 首笔
    try {
        attemptState.lastResult = await confirm(args);
        if (attemptState.lastResult.status === 'PAID') {
            return attemptState.lastResult;
        }
    } catch (err) {
        if (applyConfirmAttemptError(err, attemptState)) {
            return attemptState.lastResult!;
        }
    }

    // 额外轮询
    for (let i = 0; i < COMMERCE_CONFIRM_EXTRA_POLLS; i++) {
        await sleepMs(COMMERCE_CONFIRM_POLL_INTERVAL_MS);
        try {
            attemptState.lastResult = await confirm(args);
            attemptState.lastError = null;
            if (attemptState.lastResult.status === 'PAID') {
                return attemptState.lastResult;
            }
        } catch (err) {
            if (applyConfirmAttemptError(err, attemptState)) {
                return attemptState.lastResult!;
            }
        }
    }

    const { lastResult, lastError } = attemptState;

    if (lastResult) {
        return lastResult;
    }
    throw lastError instanceof Error ? lastError : new Error('confirm failed');
}
